import type { JsonObject } from 'type-fest';

import type { ShowBalance } from 'app-components/flux/calculated-balances/balanceTypes';
import type { GSFStage } from 'app-components/flux/free-to-spend/freeToSpendTypes';
import type { SplitFieldDefaults } from 'app-components/flux/transactions/splitsHelpers';
import type { LayoutItem } from 'app-components/hooks/dashboard/useDashboardLayout';

import type { PlannedSpendingListType } from './preferencesV2Types';

export const PREFERENCE_V2_NAMES = {
  aiChatFirstOpenGuideSeen: 'aiChatFirstOpenGuideSeen',
  a2aTransferEnabled: 'a2a-transfer-enabled',
  aasrCountErrorcodes: 'aasr-count-errorcodes',
  aggregatorAasrEnable: 'aggregator-aasr-enable',
  aggregatorPlatform: 'aggregator-platform',
  aiChatDisclaimerSeen: 'aiChatDisclaimerSeen',
  aiChatModel: 'aiChatModel',
  businessAssistBannerDismissed: 'businessAssistBannerDismissed',
  privacyAssistBannerDismissed: 'privacyAssistBannerDismissed',
  aiContextualPromptChipsEnabled: 'aiContextualPromptChipsEnabled',
  aiInsightsConsentDialogSeen: 'aiInsightsConsentDialogSeen',
  aiInsightsOptIn: 'aiInsightsOptIn',
  aiModelOverrideIds: 'aiModelOverrideIds',
  aiWeeklyReportEnabled: 'aiWeeklyReportEnabled',
  allowA2aCreditOnDebitDay: 'allow-a2a-credit-on-debit-day',
  allowRppsCreditOnDebitDay: 'allow-rpps-credit-on-debit-day',
  assignFilterType: 'assign-filter-type',
  autoDelayedLoginsDeletion: 'auto-delayed-logins-deletion',
  autoReleasePastExpenses: 'auto-release-past-expenses',
  autoReleaseUnusedFunds: 'auto-release-unused-funds',
  bpsMakeEbnPaymentDefault: 'bps-make-ebn-payment-default',
  calendarRangeDefault: 'calendar-range-default',
  colorMode: 'colorMode',
  copyNormalizedBalance: 'copy-normalized-balance',
  creditScoreInfoDisplayed: 'creditScoreInfoDisplayed',
  darkMode: 'DarkMode',
  digestMockEnabled: 'digestMockEnabled',
  dashboardTaskDismissedCards: 'dashboardTaskDismissedCards',
  dateOnAfter: 'dateOnAfter',
  disableBatchRefresh: 'disable-batch-refresh',
  dontShowApplyToExistingTxnDlg: 'dontShowApplyToExistingTxnDlg',
  dontShowRuleCreatedDlg: 'dontShowRuleCreatedDlg',
  enableAccountsPayloadLog: 'enable-accounts-payload-log',
  enableAggregationDebugLogs: 'enable-aggregation-debug-logs',
  enableAllNotifications: 'enable-all-notifications',
  enableClientVersionValidation: 'enable-client-version-validation',
  enableFiOpenSearch: 'enable-fi-open-search',
  enableInvestmentTransactions: 'enable-investment-transactions',
  enableLogCpResponse: 'enable-log-cp-response',
  fdpAcmeDefaultFiPlatform: 'fdp-acme-default-fi-platform',
  fdpIconsEnabled: 'fdp-icons-enabled',
  fiAggregator: 'fi-aggregator',
  gsfScreen: 'gsfScreen',
  hideClosedAccBar: 'hideClosedAccBar',
  hideGoalsAccBar: 'hideGoalsAccBar',
  identityPending: 'identityPending',
  ifsRppsBillpay: 'ifs-rpps-billpay',
  ifsRppsBillpayDataset: 'ifs-rpps-billpay-dataset',
  ifsTransferA2a: 'ifs-transfer-a2a',
  invAccountsByInstitution: 'invAccountsByInstitution',
  investmentAccountsByInstitution: 'investmentAccountsByInstitution',
  invoicePaymentProvider: 'invoice-payment-provider',
  netWorthShowPercent: 'netWorthShowPercent',
  plannedSpendingView: 'plannedSpendingView',
  preFiMigrationSupportedBrandingIds: 'pre-fi-migration-supported-branding-ids',
  profileComplete: 'profileComplete',
  qemActivityOverview: 'qemActivityOverview',
  qemItemSectionCollapse: 'qemItemSectionCollapse',
  qemItemSectionOrder: 'qemItemSectionOrder',
  qemLayoutVariant: 'qemLayoutVariant',
  qemMFAPromptDismiss: 'qemMFAPromptDismiss',
  qemNewItemHintDismissed: 'qemNewItemHintDismissed',
  qemQuickAddCard: 'qemQuickAddCard',
  qemRecommendedGuideCompleted: 'qemRecommendedGuideCompleted',
  qemRecommendedGuideExpanded: 'qemRecommendedGuideExpanded',
  qemSectionExpansion: 'qemSectionExpansion',
  qemSecurityOverviewExpansion: 'qemSecurityOverviewExpansion',
  qemShowPartnerOffer: 'qemShowPartnerOffer',
  qemShowZeroStateBanner: 'qemShowZeroStateBanner',
  qemShowZeroStateBannerPref: 'qemShowZeroStateBannerPref',
  qemSmartFoldersOverviewExpansion: 'qemSmartFoldersOverviewExpansion',
  qemSortOrderPref: 'qemSortOrderPref',
  qemTagsShowGettingStarted: 'qemTagsShowGettingStarted',
  qemWaitlist: 'qemWaitlist',
  qisImportId: 'qis-import-id',
  qlhAutoLogoutTimeout: 'qlhAutoLogoutTimeout',
  recommendedGuideCompleted: 'recommendedGuideCompleted',
  recommendedGuideExpanded: 'recommendedGuideExpanded',
  relatedNewsCollapse: 'relatedNewsCollapse',
  reportGridDisplayOptions: 'report-grid-display-options',
  reviewListTopAlertShow: 'reviewListTopAlertShow',
  sAccountBarOrder: 'sAccountBarOrder',
  sAccountReorderBanner: 'sAccountReorderBanner',
  sAccountSummaryLayoutMobile: 'sAccountSummaryLayoutMobile',
  sAccountsBankBalance: 'sAccountsBankBalance',
  sBarchartAdvanced: 'sBarchartAdvanced',
  sBarchartPeriod: 'sBarchartPeriod',
  sBillScoutSetupAddSeriesPromptShown: 'sBillScoutSetupAddSeriesPromptShown',
  sBillScoutSetupDismissed: 'sBillScoutSetupDismissed',
  sBillScoutSetupProgress: 'sBillScoutSetupProgress',
  sCostBasisBanner: 'sCostBasisBanner',
  sCashflowLegendGroupExpansion: 'sCashflowLegendGroupExpansion',
  sCashflowLegendIncludedIds: 'sCashflowLegendIncludedIds',
  sCreditCardViewMode: 'sCreditCardViewMode',
  sDismissBillScoutIntro: 'sDismissBillScoutIntro',
  sDismissReviewColumnTooltip: 'sDismissReviewColumnTooltip',
  sDismissedTaxPlanBanners: 'sDismissedTaxPlanBanners',
  sF2STransactionGrid: 'sF2STransactionGrid',
  sFutureTxnGuideMobile: 'sFutureTxnGuideMobile',
  sFutureTxnGuideWeb: 'sFutureTxnGuideWeb',
  sGsfSelectedGoals: 'sGsfSelectedGoals',
  sGsfSelectedFis: 'sGsfSelectedFis',
  sHoldingsGrid: 'sHoldingsGrid',
  sInvestmentTransactionGrid: 'sInvestmentTransactionGrid',
  sInvestmentWatchList: 'sInvestmentWatchList',
  sNavExpandedWeb: 'sNavExpandedWeb',
  sOverviewPageLayoutMobile: 'sOverviewPageLayoutMobile',
  sOverviewPageLayoutWeb: 'sOverviewPageLayoutWeb',
  sPrivacyMode: 'sPrivacyMode',
  sReceiptMatchGuide: 'sReceiptMatchGuide',
  sReportFilterBanner: 'sReportFilterBanner',
  sReportsTransactionGrid: 'sReportsTransactionGrid',
  sSpendingDrawerTransactionGrid: 'sSpendingDrawerTransactionGrid',
  sSpendingDashboardTransactionGrid: 'sSpendingDashboardTransactionGrid',
  sSPExcludeIncomeByDefault: 'sSPExcludeIncomeByDefault',
  sSPGettingStartedFlowPhase: 'sSPGettingStartedFlowPhase',
  sShowExcludedBillsBanner: 'sShowExcludedBillsBanner',
  sShowExcludedIncomeBanner: 'sShowExcludedIncomeBanner',
  sShowKbbLinkBanner: 'sShowKbbLinkBanner',
  sShowLifeHubBanner: 'sShowLifeHubBanner',
  sShowReviewTransactionInfoMobile: 'sShowReviewTransactionInfoMobile',
  sSortPlannedItemsByAmount: 'sSortPlannedItemsByAmount',
  sTaxIncomeDeductionsBanner: 'sTaxIncomeDeductionsBanner',
  sTaxPaymentsBanner: 'sTaxPaymentsBanner',
  sTransactionGrid: 'sTransactionGrid',
  sTransactionGridDisplay: 'sTransactionGridDisplay',
  sWarnMovedCreditScore: 'sWarnMovedCreditScore',
  sWarnMovedRefund: 'sWarnMovedRefund',
  // One per platform, since the two sort menus differ. Both replace web's v1 `webApp.swlOrder` and
  // are DATASET-scoped. Deliberately unlike `netWorthShowPercent` / `sAccountBarOrder` /
  // `sSortPlannedItemsByAmount`, which share a single preference across platforms.
  sWatchlistSortOrderMobile: 'sWatchlistSortOrderMobile',
  sWatchlistSortOrderWeb: 'sWatchlistSortOrderWeb',
  selectedDatasetId: 'selectedDatasetId',
  showHistoricalBalancesWarning: 'showHistoricalBalancesWarning',
  showZillowLinkBanner: 'showZillowLinkBanner',
  spendingPlanBillGrouping: 'spendingPlanBillGrouping',
  splitsAlwaysShowFields: 'splitsAlwaysShowFields',
  suggestedPSICategoryKnownIds: 'suggestedPSICategoryKnownIds',
  sTransactionDetailFeedbackSubmitted: 'sTransactionDetailFeedbackSubmitted',
  transactionDialogPreferences: 'transactionDialogPreferences',
  transactionFlowTestingEnabled: 'transaction-flow-testing-enabled',
  txnAllowInternalTransferOnly: 'txn-allow-internal-transfer-only',
  txnCreateOtherSideTransferMatching: 'txn-create-other-side-transfer-matching',
  txnInitialUpdateLimit: 'txn-initial-update-limit',
  txnLogMlTxnSimilarityMatchingScore: 'txn-log-ml-txn-similarity-matching-score',
  txnPastDaysLimit: 'txn-past-days-limit',
  txnPreferAggDateWhenMatching: 'txn-prefer-agg-date-when-matching',
  txnServiceEnableAccountBillProcessing: 'txn-service-enable-account-bill-processing',
  txnUseMlInferredPayee: 'txn-use-ml-Inferred-payee',
  txnUseMlInvestmentsData: 'txn-use-ml-investments-data',
  txnUseMlPayeeSimilarityMatching: 'txn-use-ml-payee-similarity-matching',
  useTransactionRules: 'use-transaction-rules',
  userTheme: 'userTheme',
} as const;

export type PreferenceV2NameKey = keyof typeof PREFERENCE_V2_NAMES;
export type PreferenceV2Name = (typeof PREFERENCE_V2_NAMES)[PreferenceV2NameKey];

type NameOf<Key extends PreferenceV2NameKey> = (typeof PREFERENCE_V2_NAMES)[Key];

/** Column state the web data grids persist; older saves lack fields. */
type DataGridPreferenceV2 = {
  version?: string;
  columnsOrder?: string[];
  columnVisibilityModel?: Record<string, boolean>;
  columnWidths?: Record<string, { flex?: number; width?: number }>;
  pinnedColumns?: { left?: string[]; right?: string[] };
};

export type DataGridPreferenceV2Name = NameOf<
  | 'sF2STransactionGrid'
  | 'sHoldingsGrid'
  | 'sInvestmentTransactionGrid'
  | 'sReportsTransactionGrid'
  | 'sSpendingDashboardTransactionGrid'
  | 'sSpendingDrawerTransactionGrid'
  | 'sTransactionGrid'
>;

/** Display options the web register persists next to its column state. */
type DataGridDisplayPreferenceV2 = { splitViewMode?: 'parent' | 'split-item' };

export type DataGridDisplayPreferenceV2Name = NameOf<'sTransactionGridDisplay'>;

/** The part of the Barchart widget's saved state that the apps read. */
export type BarchartStatePreferenceV2 = { data?: { aggregation?: { dividendsAdjust?: boolean } } };

/** Requires an entry for every name and nothing else. */
type CompletePreferenceV2Values<
  Values extends Record<PreferenceV2NameKey, unknown> & Record<Exclude<keyof Values, PreferenceV2NameKey>, never>,
> = Values;

/**
 * What the store holds for each preference, keyed like PREFERENCE_V2_NAMES: the value normalizeValue
 * produces for its dataType, narrowed to what the server default and our writers put there, plus what
 * else really lands there (serialized strings, nulls, partial or unrecognized values other clients
 * saved). `unknown` marks preferences web and mobile never read or write.
 * Not modeled: a hook write made before the record loads goes out without its dataType, so an array
 * saved to an ARRAY preference that way is stored as String(array) and reads back as null or a number.
 */
type PreferenceV2ValueTypes = CompletePreferenceV2Values<{
  aiChatFirstOpenGuideSeen: boolean;
  a2aTransferEnabled: unknown;
  aasrCountErrorcodes: unknown;
  aggregatorAasrEnable: unknown;
  aggregatorPlatform: unknown;
  aiChatDisclaimerSeen: boolean;
  aiChatModel: unknown;
  businessAssistBannerDismissed: boolean;
  privacyAssistBannerDismissed: boolean;
  aiContextualPromptChipsEnabled: boolean;
  aiInsightsConsentDialogSeen: boolean;
  aiInsightsOptIn: boolean;
  aiModelOverrideIds: unknown;
  aiWeeklyReportEnabled: boolean;
  allowA2aCreditOnDebitDay: unknown;
  allowRppsCreditOnDebitDay: unknown;
  assignFilterType: unknown;
  autoDelayedLoginsDeletion: unknown;
  autoReleasePastExpenses: unknown;
  // 'off', or the month auto-release was turned on.
  autoReleaseUnusedFunds: string;
  bpsMakeEbnPaymentDefault: unknown;
  // Date range ids are read case-insensitively.
  calendarRangeDefault: { upcoming?: string };
  colorMode: unknown;
  copyNormalizedBalance: unknown;
  creditScoreInfoDisplayed: boolean;
  darkMode: unknown;
  digestMockEnabled: boolean;
  dashboardTaskDismissedCards: unknown;
  dateOnAfter: string;
  disableBatchRefresh: unknown;
  dontShowApplyToExistingTxnDlg: unknown;
  dontShowRuleCreatedDlg: boolean;
  enableAccountsPayloadLog: unknown;
  enableAggregationDebugLogs: unknown;
  enableAllNotifications: unknown;
  enableClientVersionValidation: unknown;
  enableFiOpenSearch: unknown;
  enableInvestmentTransactions: unknown;
  enableLogCpResponse: unknown;
  fdpAcmeDefaultFiPlatform: unknown;
  fdpIconsEnabled: unknown;
  fiAggregator: string;
  // Usually a GsfStage, but the web flow writes its config's step names, and '' for a step with no next.
  gsfScreen: string;
  hideClosedAccBar: boolean;
  hideGoalsAccBar: boolean;
  identityPending: boolean;
  ifsRppsBillpay: unknown;
  ifsRppsBillpayDataset: unknown;
  ifsTransferA2a: unknown;
  invAccountsByInstitution: unknown;
  investmentAccountsByInstitution: boolean;
  invoicePaymentProvider: unknown;
  netWorthShowPercent: boolean;
  plannedSpendingView: NonNullable<PlannedSpendingListType>;
  preFiMigrationSupportedBrandingIds: unknown;
  profileComplete: unknown;
  qemActivityOverview: unknown;
  qemItemSectionCollapse: unknown;
  qemItemSectionOrder: unknown;
  qemLayoutVariant: unknown;
  qemMFAPromptDismiss: unknown;
  qemNewItemHintDismissed: unknown;
  qemQuickAddCard: unknown;
  qemRecommendedGuideCompleted: unknown;
  qemRecommendedGuideExpanded: unknown;
  qemSectionExpansion: unknown;
  qemSecurityOverviewExpansion: unknown;
  qemShowPartnerOffer: unknown;
  qemShowZeroStateBanner: unknown;
  qemShowZeroStateBannerPref: unknown;
  qemSmartFoldersOverviewExpansion: unknown;
  qemSortOrderPref: unknown;
  qemTagsShowGettingStarted: unknown;
  qemWaitlist: unknown;
  qisImportId: unknown;
  qlhAutoLogoutTimeout: unknown;
  recommendedGuideCompleted: unknown;
  recommendedGuideExpanded: unknown;
  relatedNewsCollapse: boolean;
  reportGridDisplayOptions: unknown;
  reviewListTopAlertShow: unknown;
  // Business id to its position in the account bar.
  sAccountBarOrder: Record<string, number>;
  sAccountReorderBanner: boolean;
  // useDashboardLayout writes the layout serialized; the server returns it parsed.
  sAccountSummaryLayoutMobile: LayoutItem[] | string;
  sAccountsBankBalance: (typeof ShowBalance)[keyof typeof ShowBalance];
  // Resetting the chart on web writes null.
  sBarchartAdvanced: BarchartStatePreferenceV2 | null;
  // QCS can return the period in a different case.
  sBarchartPeriod: string;
  sBillScoutSetupAddSeriesPromptShown: unknown;
  sBillScoutSetupDismissed: boolean;
  // Old clients saved partial progress and newer ones may change its shape; readers normalize it.
  sBillScoutSetupProgress: JsonObject;
  sCostBasisBanner: boolean;
  // Written serialized when the preference's dataType is STRING.
  sCashflowLegendGroupExpansion: Record<string, boolean> | string;
  sCashflowLegendIncludedIds: string[] | string;
  sCreditCardViewMode: string;
  sDismissBillScoutIntro: boolean;
  sDismissReviewColumnTooltip: boolean;
  // Tax years; a missing year is saved as null.
  sDismissedTaxPlanBanners: Array<number | null | undefined>;
  sF2STransactionGrid: DataGridPreferenceV2;
  sFutureTxnGuideMobile: boolean;
  sFutureTxnGuideWeb: boolean;
  // Goal ids, including ones a newer client added; readers drop the ones they don't know.
  sGsfSelectedGoals: string[];
  sGsfSelectedFis: string[];
  sHoldingsGrid: DataGridPreferenceV2;
  sInvestmentTransactionGrid: DataGridPreferenceV2;
  sInvestmentWatchList: unknown;
  sNavExpandedWeb: boolean;
  sOverviewPageLayoutMobile: LayoutItem[] | string;
  sOverviewPageLayoutWeb: LayoutItem[] | string;
  sPrivacyMode: boolean;
  sReceiptMatchGuide: boolean;
  sReportFilterBanner: boolean;
  sReportsTransactionGrid: DataGridPreferenceV2;
  sSpendingDrawerTransactionGrid: DataGridPreferenceV2;
  sSpendingDashboardTransactionGrid: DataGridPreferenceV2;
  sSPExcludeIncomeByDefault: boolean;
  sSPGettingStartedFlowPhase: GSFStage;
  sShowExcludedBillsBanner: boolean;
  sShowExcludedIncomeBanner: boolean;
  sShowKbbLinkBanner: boolean;
  sShowLifeHubBanner: boolean;
  sShowReviewTransactionInfoMobile: boolean;
  sSortPlannedItemsByAmount: boolean;
  sTaxIncomeDeductionsBanner: unknown;
  sTaxPaymentsBanner: boolean;
  sTransactionGrid: DataGridPreferenceV2;
  sTransactionGridDisplay: DataGridDisplayPreferenceV2;
  sWarnMovedCreditScore: unknown;
  sWarnMovedRefund: unknown;
  // Can hold an order this client doesn't offer; readers normalize it.
  sWatchlistSortOrderMobile: string;
  sWatchlistSortOrderWeb: string;
  selectedDatasetId: string;
  showHistoricalBalancesWarning: boolean;
  showZillowLinkBanner: boolean;
  spendingPlanBillGrouping: boolean;
  // Keys can be missing; an empty JSON value reads back as {}.
  splitsAlwaysShowFields: Partial<SplitFieldDefaults>;
  suggestedPSICategoryKnownIds: string[];
  // Reads back as 'true' or 'false' when QCS types it STRING.
  sTransactionDetailFeedbackSubmitted: boolean | 'true' | 'false';
  transactionDialogPreferences: unknown;
  transactionFlowTestingEnabled: unknown;
  txnAllowInternalTransferOnly: unknown;
  txnCreateOtherSideTransferMatching: unknown;
  txnInitialUpdateLimit: unknown;
  txnLogMlTxnSimilarityMatchingScore: unknown;
  txnPastDaysLimit: unknown;
  txnPreferAggDateWhenMatching: unknown;
  txnServiceEnableAccountBillProcessing: unknown;
  txnUseMlInferredPayee: unknown;
  txnUseMlInvestmentsData: unknown;
  txnUseMlPayeeSimilarityMatching: unknown;
  useTransactionRules: unknown;
  userTheme: unknown;
}>;

type PreferenceV2ValuesByName = {
  [Key in PreferenceV2NameKey as NameOf<Key>]: PreferenceV2ValueTypes[Key];
};

/** The value the store holds for preference `Name`. */
export type PreferenceV2Value<Name extends PreferenceV2Name> = PreferenceV2ValuesByName[Name];
